/**
 * useMutate — lightweight replacement for @tanstack/react-query's useMutation.
 *
 * Usage:
 *   const { mutate, isPending, error } = useMutate(
 *     async (input) => await someApiCall(input),
 *     { onSuccess: (result, input) => { ... }, onError: (err) => { ... } }
 *   );
 */

import { useCallback, useRef, useState } from "react";

export interface MutateOptions<TResult, TInput> {
  onSuccess?: (result: TResult, input: TInput) => unknown;
  onError?: (error: Error, input: TInput) => void;
  onSettled?: () => void;
}

export interface UseMutateResult<TResult, TInput> {
  mutate: (input: TInput) => void;
  mutateAsync: (input: TInput) => Promise<TResult>;
  isPending: boolean;
  error: Error | null;
  data: TResult | undefined;
  reset: () => void;
}

export function useMutate<TResult, TInput = void>(
  mutationFn: (input: TInput) => Promise<TResult>,
  options?: MutateOptions<TResult, TInput>
): UseMutateResult<TResult, TInput> {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<TResult | undefined>(undefined);

  // Keep options in a ref to avoid re-renders
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutateAsync = useCallback(
    async (input: TInput): Promise<TResult> => {
      setIsPending(true);
      setError(null);
      try {
        const result = await mutationFn(input);
        setData(result);
        await optionsRef.current?.onSuccess?.(result, input);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        optionsRef.current?.onError?.(error, input);
        throw error;
      } finally {
        setIsPending(false);
        optionsRef.current?.onSettled?.();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutationFn]
  );

  const mutate = useCallback(
    (input: TInput) => {
      void mutateAsync(input);
    },
    [mutateAsync]
  );

  const reset = useCallback(() => {
    setError(null);
    setData(undefined);
  }, []);

  return { mutate, mutateAsync, isPending, error, data, reset };
}
